import { cloneDeep, isEqual, omit } from 'lodash';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { uuid } from 'short-uuid';

import { ServiceContractPositionKind } from '@work4all/models/lib/Enums/ServiceContractPositionKind.enum';

// ToDo: WW-7459 Move to shared shadow folder (4 below)
import { arrayMove } from '../../../../../../../erp/components/tab-panels/positions/components/edit-table/hooks/arrayMove';
import { getIdsIncomingKeys } from '../../../../../../../erp/components/tab-panels/positions/components/edit-table/hooks/getIdsIncomingKeys';
import { removeDotsInKeys } from '../../../../../../../erp/components/tab-panels/positions/components/edit-table/hooks/removeDotsInKeys';
import { writeObject } from '../../../../../../../erp/components/tab-panels/positions/components/edit-table/hooks/writeObject';
import {
  EditTableEntry,
  EditTablePositionKind,
  IdArray,
  OnEditComponent,
} from '../types';

interface EditableActions<T extends EditTableEntry, TContext, TId> {
  onAddComponent?: (context?: TContext) => void;
  onMoveComponent?: (
    positionId: TId,
    index: number,
    localIndex?: number
  ) => void;
  onRemoveComponent?: (positionId: TId[]) => void;
  onCloneComponent?: (positionId: TId[]) => void;
  onEditComponent?: (result: OnEditComponent<T>) => void;
}

type InEditableActions<T extends EditTableEntry, TContext> = EditableActions<
  T,
  TContext,
  T['id']
>;

export interface UseEditableStateProps<T extends EditTableEntry, TContext>
  extends InEditableActions<T, TContext> {
  positions: T[];
  mapAddLocal?: (context: TContext) => T;
  mapRemoveLocal?: (positionId: T['id'][]) => T['id'][];
  mutateState?: (inputs: T[]) => EditTableResultEntry<T>[];
  canAddRemote?: (context: TContext) => boolean;
}

export interface UseEditableStateResult<
  T extends EditTableEntry,
  TContext = EditStateContext
> extends EditableActions<T, TContext, string | T['id']> {
  allPositions: T[];
  positions: T[];
}

interface AddAction<T extends EditTableEntry> {
  type: 'add';
  newEntry: T;
  index?: number;
}

interface RemoveAction<T extends EditTableEntry> {
  type: 'remove';
  positionId: T['id'][] | string[];
}

interface MergeAction<T extends EditTableEntry> {
  type: 'merge';
  positions: T[];
}

interface EditAction<T extends EditTableEntry> {
  type: 'edit';
  result: OnEditComponent<T>;
}

interface MoveAction<T extends EditTableEntry> {
  type: 'move';
  positionId: T['id'] | string;
  index: number;
}

type PositionStateAction<T extends EditTableEntry> =
  | AddAction<T>
  | RemoveAction<T>
  | MergeAction<T>
  | EditAction<T>
  | MoveAction<T>;

const createDataFetchReducer =
  <T extends EditTableEntry>(
    mutateState: UseEditableStateProps<T, unknown>['mutateState']
  ) =>
  (
    state: EditTableResultEntry<T>[],
    action: PositionStateAction<T>
  ): EditTableResultEntry<T>[] => {
    let newState: EditTableResultEntry<T>[] = [];
    switch (action.type) {
      case 'add': {
        // For now we support only single cache row
        const cacheOnlyIndex = state.findIndex((x) => x.cacheOnly);
        const oldState = [...state].filter((x) => !x.cacheOnly);
        newState = [...oldState, action.newEntry];

        let to = action.index;
        if (to !== undefined) {
          const from = newState.length - 1;
          // if we filtered out cache row we need to move index
          if (cacheOnlyIndex > 0 && cacheOnlyIndex < to) to--;
          const p = [...newState];
          arrayMove(p, from, to);
          newState = p;
        }
        break;
      }
      case 'merge': {
        const finalState: T[] = [];
        const serverState = action.positions || [];
        serverState.forEach((pos, idx) => {
          const clonedPos = cloneDeep(pos);
          const localPos = state.find((x) => x.localId === pos.localId);

          // only if no local state there could be undefined
          if (
            state.length &&
            localPos?.dirtyFields &&
            Object.entries(localPos.dirtyFields).length
          ) {
            Object.entries(localPos.dirtyFields).forEach(
              (x) => (clonedPos[x[0]] = x[1])
            );
          }

          if (!clonedPos.localId) clonedPos.localId = `${clonedPos.id}`;

          clonedPos.index = idx;
          finalState.push(clonedPos);
        });

        newState = finalState;
        break;
      }
      case 'remove':
        newState = state.filter(
          (x) =>
            // @ts-expect-error T['id'] has type of given T entity
            !action.positionId.includes(x.id) &&
            !action.positionId.includes(x.localId)
        );
        break;
      case 'edit': {
        const { position } = action.result;
        newState = state.map((pos) => {
          delete pos.dirtyFields;
          if (pos.id !== position.id) return pos;
          const val = cloneDeep(pos);
          writeObject(position, val);
          return val;
        });
        break;
      }
      default:
        newState = state;
    }
    return mutateState ? mutateState(newState) : newState;
  };

export type EditTableResultEntry<T> = T & {
  relation?: 'parent' | 'child' | 'none';
  collapsed?: boolean;
};

export interface BaseStateContext {
  index?: number;
  localId?: string;
  cacheOnly?: boolean;
}

export type EditStateContext = Record<string, unknown> & BaseStateContext;
/**
 * State management which is used for EditTable component.
 * It organizes local & remote updates and provides utility functions
 * to manage positions in the table dynamically.
 *
 * The hook leverages React's reducer for state management, and includes
 * logic for adding, removing, editing, moving, and merging table rows.
 * It also handles both client-side updates and interactions with a remote server.
 *
 * @template T The type of table entries, extending `EditTableEntry`.
 * @template TId The type of the unique identifier for table entries.
 * @template TContext The type of the context object used for certain operations.
 *
 * @param props Object containing configuration and callbacks for managing editable table state.
 * @param props.positions Initial positions in the editable table.
 * @param props.onAddComponent Callback to handle adding a new position (optional).
 * @param props.onMoveComponent Callback to handle moving a position (optional).
 * @param props.onRemoveComponent Callback to handle removing positions (optional).
 * @param props.onEditComponent Callback to handle editing an existing position (optional).
 * @param props.mapAddLocal Function to map a local context to a new position (optional).
 * @param props.mapRemoveLocal Function to map IDs to be removed locally (optional).
 * @param props.mutateState Function to shape or mutate the current state (optional).
 * @param props.canAddRemote Function to determine if a remote position can be added (optional).
 *
 * @returns {UseEditableStateResult<T, TId, TContext>} Object containing:
 * - `positions`: Current table positions, including any local modifications.
 * - `allPositions`: All positions, both local and remote.
 * - `onAddComponent`: Callback to add a new position.
 * - `onMoveComponent`: Callback to move a position.
 * - `onRemoveComponent`: Callback to remove positions.
 * - `onEditComponent`: Callback to edit a position.
 */
export function useEditableState<
  T extends EditTableEntry,
  TContext extends BaseStateContext = EditStateContext
>(
  props: UseEditableStateProps<T, TContext>
): UseEditableStateResult<T, TContext> {
  const {
    positions,
    onAddComponent: onAddComponentOnServer,
    onMoveComponent: onMoveComponentOnServer,
    // ToDo: add on API level
    onRemoveComponent: onRemovePositionOnServer,
    onEditComponent: onEditComponentOnServer,
    mapAddLocal,
    mapRemoveLocal,
    mutateState,
    canAddRemote,
  } = props;

  const reducer = createDataFetchReducer<T>(mutateState);

  const [state, dispatch] = useReducer(reducer, []);

  /**
   * Removes one or more positions both locally and remotely.
   * Dispatches a remove action to the reducer and calls the server callback.
   */
  const onRemoveComponent = useCallback(
    (positionId: IdArray) => {
      if (mapRemoveLocal)
        dispatch({ type: 'remove', positionId: mapRemoveLocal(positionId) });
      else dispatch({ type: 'remove', positionId });

      if (positionId.filter(Boolean).length) {
        const toRemoveIds = getIdsIncomingKeys(positions, positionId);
        onRemovePositionOnServer?.(toRemoveIds);
      }
    },
    [onRemovePositionOnServer, mapRemoveLocal, positions]
  );

  const delayedUpdates = useRef<T[]>([]);
  /**
   * Effect to process delayed updates, syncing local updates to the remote server.
   */
  useEffect(() => {
    const pendingChanges = delayedUpdates.current
      .map((change) => {
        const persisted = positions.find(
          (y) => y.localId === change.localId && y.id
        );
        return {
          persisted,
          change,
        };
      })
      .filter((x) => x.persisted);
    pendingChanges.forEach(({ persisted, change }) => {
      const position = {
        position: { ...omit(change, 'localId'), id: persisted.id } as T,
      };
      onEditComponentOnServer?.(position);
      delayedUpdates.current = delayedUpdates.current.filter(
        (x) => x.localId !== change.localId
      );
    });
  }, [positions, onEditComponentOnServer, state]);

  /**
   * Edits an existing position, syncing updates both locally and remotely.
   * Applies changes if they differ from the current state.
   */
  const onEditComponent = useCallback(
    (resultIn: OnEditComponent<T>) => {
      const result = removeDotsInKeys(resultIn);
      const entry = positions.find(
        (x) =>
          (result.position.localId &&
            x.localId &&
            x.localId === result.position.localId) ||
          (result.position?.id && x.id && x.id === result.position?.id)
      );

      if (!entry?.id) {
        delayedUpdates.current.push(result.position);
        return;
      }
      const finalChange = Object.entries(result.position).filter((x) => {
        const [field, value] = x;

        if (field === 'positionKind') return true;
        if (result.forceUpdate) return true;
        if (typeof value === 'object') return true;
        return !isEqual(entry[field], value);
      });

      const position: T = finalChange.reduce((prev, [field, value]) => {
        prev[field] = value;
        return prev;
      }, {} as T);
      position.id = entry.id;

      if (!finalChange.length) {
        if ('positionKind' in result.position) {
          position.positionKind = result.position.positionKind;
        } else {
          return;
        }
      }

      dispatch({ type: 'edit', result: { position } });
      onEditComponentOnServer?.({ position: omit(position, 'localId') as T });
    },
    [onEditComponentOnServer, positions]
  );

  const lastAdded = useRef<string>(null);
  /**
   * Adds a new component to the table locally and optionally to the server.
   */
  const onAddComponent = useCallback(
    function (result: TContext) {
      if (mapAddLocal) {
        result.localId ??= uuid();
        lastAdded.current = result.localId;
        dispatch({
          type: 'add',
          newEntry: mapAddLocal(result),
          index: result.index,
        });
      }
      if (canAddRemote?.(result) ?? true) {
        onAddComponentOnServer?.(result);
      }
    },
    [onAddComponentOnServer, mapAddLocal, canAddRemote]
  );

  const onCloneComponent = useCallback(
    (copyComponents: IdArray) => {
      const ids = getIdsIncomingKeys(state, copyComponents);
      const newComponents = state.filter((x) => ids.includes(x.id));
      newComponents.forEach((x) =>
        onAddComponent({ ...x, localId: uuid() } as unknown as TContext)
      );
    },
    [state, onAddComponent]
  );

  /**
   * Moves a component to a different index within the table.
   */
  const onMoveComponent = useCallback(
    function (localId: string, index: number, localIndex?: number) {
      dispatch({
        type: 'move',
        positionId: localId,
        index: localIndex ?? index,
      });

      const remoteId = positions.find((x) => x.localId === localId)?.id;
      // console.log('onMoveComponent', positions, localIndex ?? index);
      if (remoteId) onMoveComponentOnServer?.(remoteId, index, -1);
    },
    [onMoveComponentOnServer, positions]
  );

  /**
   * Initializes or merges positions into the state when the `positions` prop changes.
   */
  useEffect(() => {
    dispatch({ type: 'merge', positions });
  }, [positions]);

  const collapsedHeads = useRef<T[]>(
    positions.filter((x) => COLLAPSED_HEAD_GROUP.includes(x.positionKind))
  );

  const lastState = useRef(state);

  const resultState = useMemo(() => {
    const newlyAddedPosition = state.find(
      (x) => x.localId === lastAdded.current
    );

    if (newlyAddedPosition) {
      lastAdded.current = '';
    }

    const headsIds = collapsedHeads.current.map((x) => x.id);
    const newState = state
      .filter((x) => !headsIds.includes(x.posId))
      .map((x) => {
        if (
          headsIds.includes(x.id) ||
          (x.localId === lastAdded.current && newlyAddedPosition)
        )
          return { ...x, collapsed: true };
        return x;
      });

    lastState.current = newState;

    return newState;
    // we need to enforce this by setting collapse heads, but we need to modify also without rerender when new item is added
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return useMemo(
    () => ({
      positions: resultState,
      allPositions: state,
      onAddComponent,
      onMoveComponent,
      onRemoveComponent,
      onEditComponent,
      onCloneComponent,
    }),
    [
      onAddComponent,
      onMoveComponent,
      onEditComponent,
      onCloneComponent,
      onRemoveComponent,
      resultState,
      state,
    ]
  );
}

export const COLLAPSED_HEAD_GROUP: EditTablePositionKind[] = [
  ServiceContractPositionKind.UEBERNEHMEN_MIT_WARTUNGSSUMME,
  ServiceContractPositionKind.UEBERNEHMEN_MIT_E_PREIS_AUS_WARTUNGS_OBJEKT,
  ServiceContractPositionKind.UEBERNEHMEN_OHNE_PREIS,
  ServiceContractPositionKind.UEBERNEHMEN_MIT_ARTIKELPREIS,
];
